import {
  GET_ACTIVE_FILES_FAILURE,
  GET_CLOSED_FILES_FAILURE,
  GET_COVERING_FILES_FAILURE,
  GET_CURRENT_COVERAGE_FAILURE,
  GET_NOTIFICATIONS_FAILURE,
} from 'modules/dashboard/myFiles/myFiles.types';
import { GET_CLIENTS_FAILURE } from 'modules/clients/types';
import {
  GET_EXCEPTION_COMMENTS_FAILURE,
  GET_EXPENSE_FOLLOWUP_FAILURE,
  GET_RECURRING_PAYABLES_DASHBOARD_FAILURE,
} from 'modules/dashboard/types';
import { GET_INTERNAL_USERS_FAILURE } from 'modules/common/store/types';
import { GET_VOUCHER_FOLLOWUP_FAILURE } from './eaDashboard.types';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { connect } from 'react-redux';
import {
  getActiveFiles,
  getClosedFiles,
  getCoveringFiles,
  getCurrentCoverage,
  getNotifications,
} from 'modules/dashboard/myFiles/myFiles.actions';
import { getClients } from 'modules/clients/clients.actions';
import {
  getExceptionComments,
  getExpenseFollowUp,
  getRecurringPayablesDashboard,
} from 'modules/dashboard/dashboard.actions';
import { getInternalUsers } from 'modules/common/common.actions';
import { getVoucherFollowUp } from './eaDashboard.actions';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';
import EaDashboardComponent from './eaDashboard.component';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

const INITIAL_FILTERS = { clientId: null, fileId: null };
const INITIAL_PAGE = 1;
const INITIAL_ROWS_PER_PAGE = 25;

const EaDashboardContainer = (props) => {
  const { userId, internalUsers, clients } = props;

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [isLoadingLookups, setIsLoadingLookups] = useState(false);

  useEffect(() => {
    const loadLookups = async () => {
      setIsLoadingLookups(true);

      const usersResult = await props.getInternalUsers();
      if (usersResult.type === GET_INTERNAL_USERS_FAILURE) {
        props.showToast('Failed to load users', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      const clientsResult = await props.getClients();
      if (clientsResult.type === GET_CLIENTS_FAILURE) {
        props.showToast('Failed to load clients', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      setIsLoadingLookups(false);
    };

    props.setPageTitle('EA Dashboard');
    loadLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default the selector to the logged-in user once their profile is available
  useEffect(() => {
    if (userId && !selectedUserId) {
      setSelectedUserId(userId);
    }
  }, [userId, selectedUserId]);

  // Every dashboard endpoint is scoped to the selected user, so reload whenever it changes
  useEffect(() => {
    if (!selectedUserId) return;

    const loadDashboardData = async () => {
      const expensesResult = await props.getExpenseFollowUp(selectedUserId);
      if (expensesResult.type === GET_EXPENSE_FOLLOWUP_FAILURE) {
        props.showToast('Failed to load expense follow-up', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      const vouchersResult = await props.getVoucherFollowUp(selectedUserId);
      if (vouchersResult.type === GET_VOUCHER_FOLLOWUP_FAILURE) {
        props.showToast('Failed to load voucher follow-up', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      const recurringPayablesResult = await props.getRecurringPayablesDashboard(selectedUserId);
      if (recurringPayablesResult.type === GET_RECURRING_PAYABLES_DASHBOARD_FAILURE) {
        props.showToast('Failed to load active recurring payables', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      const exceptionsResult = await props.getExceptionComments(selectedUserId);
      if (exceptionsResult.type === GET_EXCEPTION_COMMENTS_FAILURE) {
        props.showToast('Failed to load exceptions', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      // My Files data is loaded up front so the tab is populated before it is opened
      const activeFilesResult = await props.getActiveFiles(selectedUserId);
      if (activeFilesResult.type === GET_ACTIVE_FILES_FAILURE) {
        props.showToast('Failed to load active files', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      const coveringFilesResult = await props.getCoveringFiles(selectedUserId);
      if (coveringFilesResult.type === GET_COVERING_FILES_FAILURE) {
        props.showToast('Failed to load covering files', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      const closedFilesResult = await props.getClosedFiles(selectedUserId, INITIAL_PAGE, INITIAL_ROWS_PER_PAGE);
      if (closedFilesResult.type === GET_CLOSED_FILES_FAILURE) {
        props.showToast('Failed to load closed files', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      const currentCoverageResult = await props.getCurrentCoverage(selectedUserId);
      if (currentCoverageResult.type === GET_CURRENT_COVERAGE_FAILURE) {
        props.showToast('Failed to load current coverage files', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }

      const notificationsResult = await props.getNotifications(selectedUserId, 'CoverageAssigned');
      if (notificationsResult.type === GET_NOTIFICATIONS_FAILURE) {
        props.showToast('Failed to load notifications', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }
    };

    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  const handleUserChange = (newUserId) => {
    setSelectedUserId(newUserId);
    setFilters(INITIAL_FILTERS);
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  return (
    <EaDashboardComponent
      users={internalUsers || []}
      selectedUserId={selectedUserId}
      loggedInUserId={userId}
      onUserChange={handleUserChange}
      clients={clients || []}
      filters={filters}
      onFilterChange={handleFilterChange}
      isLoading={isLoadingLookups}
      expenseFollowUp={props.expenseFollowUp || []}
      voucherFollowUp={props.voucherFollowUp || []}
      recurringPayables={props.recurringPayables || []}
      exceptionComments={props.exceptionComments || []}
      isLoadingExpenses={props.isLoadingExpenses}
      isLoadingVouchers={props.isLoadingVouchers}
      isLoadingRecurringPayables={props.isLoadingRecurringPayables}
      isLoadingExceptionComments={props.isLoadingExceptionComments}
    />
  );
};

EaDashboardContainer.propTypes = {
  userId: PropTypes.number,
  internalUsers: PropTypes.array,
  clients: PropTypes.array,
  expenseFollowUp: PropTypes.array,
  voucherFollowUp: PropTypes.array,
  recurringPayables: PropTypes.array,
  exceptionComments: PropTypes.array,
  isLoadingExpenses: PropTypes.bool,
  isLoadingVouchers: PropTypes.bool,
  isLoadingRecurringPayables: PropTypes.bool,
  isLoadingExceptionComments: PropTypes.bool,
  setPageTitle: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  getInternalUsers: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  getExpenseFollowUp: PropTypes.func.isRequired,
  getVoucherFollowUp: PropTypes.func.isRequired,
  getRecurringPayablesDashboard: PropTypes.func.isRequired,
  getExceptionComments: PropTypes.func.isRequired,
  getActiveFiles: PropTypes.func.isRequired,
  getCoveringFiles: PropTypes.func.isRequired,
  getClosedFiles: PropTypes.func.isRequired,
  getCurrentCoverage: PropTypes.func.isRequired,
  getNotifications: PropTypes.func.isRequired,
};

EaDashboardContainer.defaultProps = {
  userId: null,
  internalUsers: [],
  clients: [],
  expenseFollowUp: [],
  voucherFollowUp: [],
  recurringPayables: [],
  exceptionComments: [],
  isLoadingExpenses: false,
  isLoadingVouchers: false,
  isLoadingRecurringPayables: false,
  isLoadingExceptionComments: false,
};

const mapStateToProps = (state) => ({
  userId: state.auth.profile?.internalUserId,
  internalUsers: state.common.internalUsers,
  clients: state.clients?.unmappedClients,

  // Expense follow-up, recurring payables and exceptions share the RC dashboard's
  // endpoints, so they also share its slice of the store
  expenseFollowUp: state.dashboard.expenseFollowUp,
  recurringPayables: state.dashboard.recurringPayables,
  exceptionComments: state.dashboard.exceptionComments,
  isLoadingExpenses: state.dashboard.isLoadingExpenses,
  isLoadingRecurringPayables: state.dashboard.isLoadingRecurringPayables,
  isLoadingExceptionComments: state.dashboard.isLoadingExceptionComments,

  voucherFollowUp: state.eaDashboard.voucherFollowUp,
  isLoadingVouchers: state.eaDashboard.isLoadingVouchers,
});

const actionCreators = {
  setPageTitle,
  showToast,
  getInternalUsers,
  getClients,
  getExpenseFollowUp,
  getVoucherFollowUp,
  getRecurringPayablesDashboard,
  getExceptionComments,
  getActiveFiles,
  getCoveringFiles,
  getClosedFiles,
  getCurrentCoverage,
  getNotifications,
};

export default connect(mapStateToProps, actionCreators)(EaDashboardContainer);
